import React, { Fragment, PropsWithChildren } from 'react';
import './App.scss';
import 'stream-chat-react/dist/css/v2/index.css';

import { createTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { palette, themeOptions } from './palette';
import {
  AdminPanelSettingsOutlined,
  Diversity1,
  Logout,
  NotificationsNoneOutlined,
  PersonOutlineOutlined,
} from '@mui/icons-material';

import { Outlet, useNavigate } from 'react-router-dom';
import { RoleBasedAccess, UserRoleProvider } from '@components';
import { useApp } from './App.hook';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  MenuList,
  PaletteMode,
  ThemeOptions,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@mui/material';
import { FeedbackFish } from '@feedback-fish/react';
import { Role } from '@app/enums/Role';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { ChatWrapper } from '@components/chat/ChatWrapper';

const App: React.FC<PropsWithChildren> = () => {
  const navigator = useNavigate();
  const cookies = useAppCookies();
  const { menuOpen, logout, toggleDrawer, avatarUrl, displayName } = useApp();
  const { communicationPreferences } = useFlags();

  // Matches what UserRoleProvider and Home call logged in
  const isAuthed = cookies.auth.available && cookies.profile.available;

  const getDesignTokens = (mode: PaletteMode): ThemeOptions => themeOptions;
  const theme = createTheme(getDesignTokens('light'));

  type NavTarget = 'ADMIN' | 'HOME' | 'PREFERENCES' | 'PROFILE' | 'OTHER';

  const navigate = (e: any) => {
    const navTarget = e.currentTarget.value as NavTarget;
    switch (navTarget) {
      case 'ADMIN':
        navigator('/admin');
        break;
      case 'PREFERENCES':
        navigator('/preferences');
        break;
      case 'HOME':
        navigator('/');
        break;
      case 'PROFILE':
        navigator('/Profile');
        break;
      case 'OTHER':
        navigator('/');
        break;
    }
    toggleDrawer(false);
  };

  const profile = cookies.profile || { firstName: '', picture: '', email: '' };
  return (
    <ThemeProvider theme={theme}>
      <UserRoleProvider>
        <AppBar
          sx={{
            backgroundColor: palette.background.default,
            marginBottom: 0,
            textAlign: 'center',
          }}
          position="sticky"
          variant={'elevation'}
          elevation={0}
        >
          {isAuthed && (
            <Toolbar>
              <IconButton
                color="primary"
                size="large"
                edge="start"
                aria-label="Open menu"
                onClick={() => toggleDrawer(true)}
              >
                <MenuIcon />
              </IconButton>
              <Drawer open={menuOpen} onClose={() => toggleDrawer(false)}>
                {profile && (
                  <Fragment>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2,
                        gap: 2,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Avatar
                        src={avatarUrl}
                        sx={{ height: 96, width: 96 }}
                        slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                      />
                      <Typography variant="h3" component="div">
                        {displayName}
                      </Typography>
                    </Box>
                    <Divider variant="fullWidth" color={palette.divider} />
                  </Fragment>
                )}
                <MenuList>
                  <MenuItem component="button" value="HOME" onClick={navigate}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                      }}
                    >
                      <Diversity1 color="primary" />
                      <Typography component="span">Home</Typography>
                    </Box>
                  </MenuItem>
                  {communicationPreferences && (
                    <MenuItem component="button" value="PROFILE" onClick={navigate}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 2,
                        }}
                      >
                        <PersonOutlineOutlined></PersonOutlineOutlined>
                        <Typography component="span">Profile</Typography>
                      </Box>
                    </MenuItem>
                  )}
                  {communicationPreferences && (
                    <MenuItem component="button" value="PREFERENCES" onClick={navigate}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 2,
                        }}
                      >
                        <NotificationsNoneOutlined color="primary" />
                        <Typography component="span">Preferences</Typography>
                      </Box>
                    </MenuItem>
                  )}

                  <RoleBasedAccess requiredRoles={[Role.Admin]}>
                    <MenuItem component="button" value="ADMIN" onClick={navigate}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 2,
                        }}
                      >
                        <AdminPanelSettingsOutlined />
                        <Typography component="span">Admin</Typography>
                      </Box>
                    </MenuItem>
                  </RoleBasedAccess>
                  <MenuItem component="button" value="OTHER" onClick={logout}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 2,
                      }}
                    >
                      <Logout></Logout>
                      <Typography component="span">Logout</Typography>
                    </Box>
                  </MenuItem>
                </MenuList>
              </Drawer>
              <Grid component="div" sx={{ flexGrow: 1, marginTop: '10px' }}>
                <img src={'/logo.png'} alt={'We Hate Exercise'} className="logo"></img>
              </Grid>

              <span></span>

              <Fragment>
                <div className="feedbackJoyride">
                  <FeedbackFish projectId="2f352c37eef603" userId={cookies.profile.email}>
                    <Button>Feedback!</Button>
                  </FeedbackFish>
                </div>
                <Button
                  id="basic-button"
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                >
                  <Avatar src={avatarUrl} slotProps={{ img: { referrerPolicy: 'no-referrer' } }}></Avatar>
                </Button>
              </Fragment>
            </Toolbar>
          )}
        </AppBar>

        {/* If the chat client is ready, wrap the router in Chat so it's accessible everywhere. Otherwise just provide the Router (most specifically so users can login) */}
        {isAuthed ? <ChatWrapper /> : <Outlet />}
      </UserRoleProvider>
    </ThemeProvider>
  );
};

export default App;
